import { useState, useEffect, useCallback, useRef } from "react";
import { Location, SpecialLocation, BilletGroup } from "../../models";
import { getLocations } from "../../repository/BilletYardInventoryRepository";
import { parseLocationsResponse } from "../../utils/parseLocationsResponse";

interface SpecificLocationDataState {
  isLoading: boolean;
  location: Location | SpecialLocation | null;
  groups: BilletGroup[];
  hasError: boolean;
  errorMessage: string;
}

interface UseSpecificLocationDataProps {
  locationId: number | null | undefined;
}

export const useSpecificLocationData = ({
  locationId,
}: UseSpecificLocationDataProps) => {
  // Track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  const [state, setState] = useState<SpecificLocationDataState>({
    isLoading: true,
    location: null,
    groups: [],
    hasError: false,
    errorMessage: "",
  });

  const fetchLocationData = useCallback(
    async (isRefresh = false) => {
      if (!locationId) {
        if (isMountedRef.current) {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
        return;
      }

      // Only show loading spinner on initial load, not on refresh
      if (!isRefresh && isMountedRef.current) {
        setState((prev) => ({ ...prev, isLoading: true, hasError: false }));
      }

      try {
        // Fetch only the specific location data needed
        const response = await getLocations(locationId.toString());

        const parsedData = parseLocationsResponse(response);
        if (parsedData) {
          // Find the location from the parsed data
          const allLocations = [
            ...parsedData.locations,
            ...parsedData.specialLocations,
          ];
          const location = allLocations.find(
            (loc: Location | SpecialLocation) => loc.LocationID === locationId,
          );

          if (location) {
            const groups = parsedData.groupsByLocationId[locationId] || [];

            // Only update state if component is still mounted
            if (isMountedRef.current) {
              setState((prev) => ({
                ...prev,
                isLoading: false,
                location,
                groups,
                hasError: false,
                errorMessage: "",
              }));
            }
          } else {
            throw new Error("Location not found in response");
          }
        } else {
          throw new Error("Invalid response format or insufficient data");
        }
      } catch (error: unknown) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            hasError: true,
            errorMessage:
              error instanceof Error
                ? error.message
                : "Failed to load location data",
          }));
        }
      }
    },
    [locationId],
  );

  useEffect(() => {
    fetchLocationData();
  }, [fetchLocationData]);

  // Cleanup effect to track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    ...state,
    refreshLocationData: (isRefresh = true) => fetchLocationData(isRefresh),
  };
};
